import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  output,
  inject,
  signal,
  OnInit,
  computed,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import {
  BaseCreationDialog,
  BaseDialogConfig,
} from '@shared/components/base-creation-dialog/base-creation-dialog';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { StepLead } from '@shared/components/step-lead/step-lead';
import { SummarySection, SummaryRow } from '@shared/components/summary-section/summary-section';
import { ComputedTotal } from '@shared/components/computed-total/computed-total';
import { EquipmentStore } from '@features/catalog/store/equipment.store';
import {
  CreateModuleDto,
  SolarModule,
} from '@features/catalog/models/module.model';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

@Component({
  selector: 'app-create-module-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ...BrnDialogImports,
    ...HlmDialogImports,
    HlmSeparatorDirective,
    WizardDialogHeader,
    WizardDialogFooter,
    SuffixInput,
    StepLead,
    SummarySection,
    SummaryRow,
    ComputedTotal,
  ],
  templateUrl: './create-module-dialog.html',
  styleUrl: './create-module-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateModuleDialog extends BaseCreationDialog implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly equipmentStore = inject(EquipmentStore);
  private readonly toastService = inject(ToastService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly destroyRef = inject(DestroyRef);

  // Input for editing
  editModule = input<SolarModule | null>(null);

  // Override config input with specific dialog configuration
  override config = input<BaseDialogConfig>({
    title: 'Neues Modul erstellen',
    description:
      'Erstellen Sie ein neues Solar-Modul mit technischen Spezifikationen und Kosten',
    stepLabels: ['Moduldetails', 'Kosten', 'Übersicht'],
    stepLeads: [
      'Technische Daten des Moduls. Die Fläche wird berechnet.',
      'Anschaffung, Montage und Installation je Modul.',
      'Prüfen Sie die Angaben vor dem Speichern.',
    ],
    submitButtonText: 'Modul erstellen',
    eyebrow: 'Katalog · Modul',
  });

  // Computed config based on edit mode for display purposes
  computedTitle = computed(() => {
    const editing = !!this.editModule();
    return editing ? 'Modul bearbeiten' : 'Neues Modul erstellen';
  });

  computedDescription = computed(() => {
    const editing = !!this.editModule();
    return editing
      ? 'Bearbeiten Sie die technischen Spezifikationen und Kosten des Moduls'
      : 'Erstellen Sie ein neues Solar-Modul mit technischen Spezifikationen und Kosten';
  });

  computedSubmitButtonText = computed(() => {
    const editing = !!this.editModule();
    return editing ? 'Modul speichern' : 'Modul erstellen';
  });

  // Outputs - delegate to the base class's submitted output
  moduleCreated = output<SolarModule>();
  moduleUpdated = output<SolarModule>();

  // Form
  moduleForm: FormGroup;

  // Calculated signals
  totalCosts = signal(0);
  areaM2 = signal(0);

  constructor() {
    super();
    this.moduleForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/\S/), Validators.minLength(3)]],
      power: ['', [Validators.required, Validators.min(0.05), Validators.max(10)]],
      height: ['', [Validators.required, Validators.min(10), Validators.max(500)]],
      width: ['', [Validators.required, Validators.min(10), Validators.max(500)]],
      efficiencyLossPerYear: ['', [Validators.required, Validators.min(0), Validators.max(5)]],
      costs: ['', [Validators.required, Validators.min(0)]],
      installationCosts: ['', [Validators.required, Validators.min(0)]],
      mountingSystemCosts: ['', [Validators.required, Validators.min(0)]],
    });

    // Subscribe to form value changes to update calculated fields
    this.moduleForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateCalculatedFields();
      });

    // Use effect to populate form when editModule changes
    effect(() => {
      const module = this.editModule();
      if (module) {
        this.populateForm(module);
      }
    });
  }

  ngOnInit() {
    // Initial calculation
    this.updateCalculatedFields();
  }

  private populateForm(module: SolarModule | null): void {
    if (module) {
      this.moduleForm.patchValue({
        name: module.name,
        power: module.power,
        height: module.height,
        width: module.width,
        efficiencyLossPerYear: module.efficiencyLossPerYear,
        costs: module.costs,
        installationCosts: module.installationCosts,
        mountingSystemCosts: module.mountingSystemCosts,
      });
    }
  }

  private updateCalculatedFields() {
    const costs = this.moduleForm.get('costs')?.value || 0;
    const installationCosts =
      this.moduleForm.get('installationCosts')?.value || 0;
    const mountingSystemCosts =
      this.moduleForm.get('mountingSystemCosts')?.value || 0;
    const height = this.moduleForm.get('height')?.value || 0;
    const width = this.moduleForm.get('width')?.value || 0;

    this.totalCosts.set(costs + installationCosts + mountingSystemCosts);
    this.areaM2.set((height * width) / 10000);
  }

  override canProceedFromCurrentStep(): boolean {
    const step = this.currentStep();
    switch (step) {
      case 1:
        // Step 1: Only check module details (name, power, height, width, efficiency)
        return (
          this.moduleForm.get('name')?.valid === true &&
          this.moduleForm.get('power')?.valid === true &&
          this.moduleForm.get('height')?.valid === true &&
          this.moduleForm.get('width')?.valid === true &&
          this.moduleForm.get('efficiencyLossPerYear')?.valid === true
        );
      case 2:
        // Step 2: Check cost fields (costs, installationCosts, mountingSystemCosts)
        return (
          this.moduleForm.get('costs')?.valid === true &&
          this.moduleForm.get('installationCosts')?.valid === true &&
          this.moduleForm.get('mountingSystemCosts')?.valid === true
        );
      case 3:
        // Step 3: All fields valid
        return this.moduleForm.valid;
      default:
        return false;
    }
  }

  // Reset dialog state
  protected override resetDialog(): void {
    super.resetDialog();
    this.moduleForm.reset();
  }

  // Handle submission
  protected override async handleSubmit(dialogContext: any): Promise<void> {
    if (!this.moduleForm.valid) {
      throw new Error('Bitte füllen Sie alle erforderlichen Felder aus');
    }

    const formData = this.moduleForm.value;
    const payload: CreateModuleDto = {
      name: formData.name.trim(),
      power: Number(formData.power),
      costs: Number(formData.costs),
      installationCosts: Number(formData.installationCosts),
      mountingSystemCosts: Number(formData.mountingSystemCosts),
      height: Number(formData.height),
      width: Number(formData.width),
      efficiencyLossPerYear: Number(formData.efficiencyLossPerYear),
    };

    const orgId = this.currentOrgStore.organizationId();
    if (!orgId) {
      throw new Error('Organisationations-ID nicht verfügbar');
    }

    const editingModule = this.editModule();
    let result: SolarModule;

    if (editingModule) {
      // Update mode
      result = await this.equipmentStore.updateModule(
        orgId,
        editingModule.id,
        payload,
      );
      this.toastService.success('Modul erfolgreich aktualisiert');
      this.submitted.emit(result);
      this.moduleUpdated.emit(result);
    } else {
      // Create mode
      result = await this.equipmentStore.createModule(orgId, payload);
      if (!result) {
        throw new Error('Fehler beim Erstellen des Moduls');
      }
      this.toastService.success('Modul erfolgreich erstellt');
      this.submitted.emit(result);
      this.moduleCreated.emit(result);
    }
  }
}
